import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import { MaskPageBack } from '@/components/mask/MaskSystem';
import { CarmaExperience } from '@/components/CarmaExperience';
import { dateLocale } from '@/lib/dateLocale';
import '@/components/mask/mask.css';

/* ICH → BEWAHREN: ruhiger Zugang zu bewahrten Carma (Daten live aus FÜR DICH, keine Doppel-Struktur) */

const fragment = (text, len = 120) => (text.length > len ? `${text.slice(0, len).trimEnd()} …` : text);

export default function BewahrtV2() {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState(null);
  const [keptMoments, setKeptMoments] = useState([]);
  const [experience, setExperience] = useState(null);

  const dateShort = (iso) => new Date(iso).toLocaleDateString(dateLocale(i18n.language), { day: 'numeric', month: 'long', year: 'numeric' });

  useEffect(() => {
    document.body.classList.add('cc-no-overlays');
    return () => document.body.classList.remove('cc-no-overlays');
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API_V2}/carma/room`, { withCredentials: true })
      .then((res) => setItems(res.data))
      .catch((err) => {
        if (err.response?.status === 401) navigate('/auth', { replace: true });
        else setItems([]);
      });
    axios.get(`${API_V2}/moments/kept`, { withCredentials: true })
      .then((res) => setKeptMoments(res.data))
      .catch(() => {});
  }, [user, API_V2, navigate]);

  const patch = (id, changes) => setItems((prev) => prev.map((m) => (m.id === id ? { ...m, ...changes } : m)));

  const keepCarma = async (id) => {
    const res = await axios.post(`${API_V2}/carma/${id}/keep`, {}, { withCredentials: true });
    patch(id, { keptAt: res.data.keptAt });
    setExperience((e) => (e && e.carma.id === id ? { ...e, carma: { ...e.carma, keptAt: res.data.keptAt } } : e));
  };
  const unkeepCarma = async (id) => {
    await axios.post(`${API_V2}/carma/${id}/unkeep`, {}, { withCredentials: true });
    patch(id, { keptAt: null });
    setExperience((e) => (e && e.carma.id === id ? { ...e, carma: { ...e.carma, keptAt: null } } : e));
  };
  const saveNote = async (id, text) => {
    const res = await axios.put(`${API_V2}/carma/${id}/note`, { text }, { withCredentials: true });
    patch(id, { note: res.data.note });
    setExperience((e) => (e && e.carma.id === id ? { ...e, carma: { ...e.carma, note: res.data.note } } : e));
  };

  if (!user || items === null) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <p className="text-sm text-[var(--muted)]">{t('v2.common.loading')}</p>
      </div>
    );
  }

  const kept = items.filter((m) => m.keptAt);

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-x-hidden" data-testid="bewahrt-page">
      <div className="mask-bg-word mask-bg-top" aria-hidden="true">
        <span style={{ transform: 'translateX(-8%)' }}>{t('v2.bgWords.bewahrt')}</span>
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-10 pb-28">
        <MaskPageBack onClick={() => navigate('/profile')} data-testid="bewahrt-back-btn" />

        <header className="mb-16">
          <p className="mask-enter text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-5">{t('v2.ich.bewahrenLabel')}</p>
          <h1 className="mask-enter mask-enter-delay-1 mask-headline text-3xl sm:text-4xl text-[var(--text)]">
            {t('v2.ich.bewahrenTitle')}
          </h1>
        </header>

        {kept.length === 0 && keptMoments.length === 0 ? (
          <div className="mask-enter mask-enter-delay-2" data-testid="bewahrt-empty">
            <p className="text-base text-[var(--muted)] leading-relaxed mb-8 max-w-md">
              {t('v2.bewahrt.empty')}
            </p>
            <button
              type="button"
              onClick={() => navigate('/inbox')}
              data-testid="bewahrt-to-inbox-btn"
              className="text-sm text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors"
            >
              {t('v2.bewahrt.toInbox')}
            </button>
          </div>
        ) : (
          <div className="space-y-12 mask-enter mask-enter-delay-2">
            {keptMoments.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => navigate(`/moment/erleben/${m.receiveToken}`)}
                data-testid={`bewahrt-moment-${m.id}`}
                className="mask-room-item block w-full text-left"
              >
                <span className="block mask-headline text-xl sm:text-2xl text-[var(--text)]/85 leading-relaxed">
                  {m.contributionCount === 1 ? t('v2.bewahrt.momentKeptOne', { count: m.contributionCount }) : t('v2.bewahrt.momentKeptMany', { count: m.contributionCount })}
                </span>
                <span className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mt-2.5">{m.occasion} · {t('v2.bewahrt.relive')}</span>
              </button>
            ))}
            {kept.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setExperience({ carma: m, isDiscovery: false })}
                data-testid={`bewahrt-item-${m.id}`}
                className="mask-room-item block w-full text-left"
              >
                <span className="block mask-headline text-xl sm:text-2xl text-[var(--text)]/85 leading-relaxed">„{fragment(m.message)}“</span>
                <span className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mt-2.5">{m.senderName} · {dateShort(m.createdAt)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {experience && (
        <CarmaExperience
          carma={experience.carma}
          isDiscovery={false}
          onOpen={() => {}}
          onKeep={keepCarma}
          onUnkeep={unkeepCarma}
          onSaveNote={saveNote}
          onClose={() => setExperience(null)}
        />
      )}
    </div>
  );
}
